import React, { useState, useEffect, useRef, useMemo } from "react"
import { createPortal } from "react-dom"
import cx from "classnames"
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd"
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi"
import { motion, AnimatePresence } from "framer-motion"
import BlurHandler from "./BlurHandler"
import ErrorBoundary from "../ErrorBoundary"

const BAR_DROP = "x-tab-bar"
const MENU_DROP = "x-tab-menu"

const TabBar = ({ index, tabs, onChange, onReorder, className, styleObj }) => {
    const tabContainer = useRef()
    const cloneLayer = useRef()
    const [widthAvailable, setWidthAvailable] = useState(0)

    const [menuIndex, setMenuIndex] = useState()
    const [showMenu, setShowMenu] = useState()

    // Re-measure the overflow when the order changes too, not just the count.
    const orderKey = tabs.map((t) => t.id).join("|")

    useEffect(() => {
        let wNeeded = 0
        let cIndex = undefined

        tabContainer.current.childNodes.forEach((node, indx) => {
            wNeeded += node?.getBoundingClientRect().width || 0

            if (cIndex === undefined && wNeeded > widthAvailable) {
                cIndex = indx
            }
        })

        setShowMenu(null)
        setMenuIndex(cIndex)
    }, [orderKey, widthAvailable])

    useEffect(() => {
        if (tabContainer.current) {
            const observer = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    setWidthAvailable(entry.contentRect.width)
                }
            })

            observer.observe(tabContainer.current)

            // Cleanup function
            return () => {
                observer.disconnect()
            }
        }
    }, [])

    if (onReorder) {
        // The bar and the overflow menu are two lists of one order: the menu starts at
        // menuIndex. Tabs can be dragged within either and across, both ways.
        const visibleCount = menuIndex === undefined ? tabs.length : menuIndex
        const toGlobal = (loc) => (loc.droppableId === MENU_DROP ? visibleCount + loc.index : loc.index)

        const onDragEnd = ({ source, destination }) => {
            if (!destination) return

            const from = toGlobal(source)
            let to = toGlobal(destination)

            // Leaving the bar for the menu shifts the menu's start left by one.
            if (source.droppableId === BAR_DROP && destination.droppableId === MENU_DROP) to -= 1
            if (from === to) return

            const ids = tabs.map((t) => t.id)
            const [moved] = ids.splice(from, 1)
            ids.splice(to, 0, moved)

            onReorder(ids)
        }

        // The menu is transformed and clips, so the dragged tab is drawn in a fixed layer
        // carrying the bar's classes instead, the same pill wherever it came from.
        const renderClone = (provided, snapshot, rubric) => {
            const tab = tabs[toGlobal(rubric.source)]
            // The library sizes the clone like the grabbed item, which from the menu is a
            // full-width row (230x41 against a 95x32 pill); let the bar's styles size it instead.
            const { width, height, ...dragStyle } = provided.draggableProps.style || {}

            return (
                <li
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={cx(tab?.className, "x-tab-dragging", { active: index === toGlobal(rubric.source) })}
                    style={{ ...tab?.style, ...dragStyle }}
                >
                    {tab?.title}
                </li>
            )
        }

        const draggableTab = (tab, i, dropIndex, onClick) => (
            <Draggable key={tab.id + "-drag"} draggableId={tab.id} index={dropIndex}>
                {(provided, snapshot) => (
                    <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={cx(tab.className, { active: index === i, "x-tab-drag-source": snapshot.isDragging })}
                        style={{ ...tab.style, ...provided.draggableProps.style }}
                        onClick={onClick}
                    >
                        {tab.title}
                    </li>
                )}
            </Draggable>
        )

        return (
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                    droppableId={BAR_DROP}
                    direction="horizontal"
                    renderClone={renderClone}
                    getContainerForClone={() => cloneLayer.current}
                >
                    {(provided) => (
                        <ul
                            className={cx("x-tab-bar", className)}
                            ref={(el) => {
                                tabContainer.current = el
                                provided.innerRef(el)
                            }}
                            {...provided.droppableProps}
                            style={{ ...(styleObj || {}) }}
                        >
                            {/* One child per tab in order, as the overflow measurement expects;
                                the overflowing ones stay hidden here and drag from the menu. */}
                            {tabs.map((tab, i) =>
                                i < visibleCount ? (
                                    draggableTab(tab, i, i, () => onChange(i))
                                ) : (
                                    <li key={tab.id + "-li"} className={cx(tab.className, "hide")} style={tab.style}>
                                        {tab.title}
                                    </li>
                                ),
                            )}

                            {provided.placeholder}

                            {menuIndex !== undefined && (
                                <li
                                    className="x-tab-more"
                                    onClick={(e) => {
                                        e.persist()
                                        setShowMenu(e)
                                    }}
                                >
                                    <PiDotsThreeOutlineVerticalFill />
                                </li>
                            )}
                        </ul>
                    )}
                </Droppable>

                <BlurHandler
                    onBlur={() => {
                        setShowMenu(null)
                    }}
                >
                    <Droppable
                        droppableId={MENU_DROP}
                        renderClone={renderClone}
                        getContainerForClone={() => cloneLayer.current}
                    >
                        {(provided) => (
                            <ul
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{ left: showMenu?.pageX, top: showMenu?.pageY }}
                                className={cx("x-tabs-more-menu", { open: Boolean(showMenu) })}
                            >
                                {tabs.slice(visibleCount).map((tab, j) =>
                                    draggableTab(tab, visibleCount + j, j, () => {
                                        onChange(visibleCount + j)
                                        setShowMenu(null)
                                    }),
                                )}
                                {provided.placeholder}
                            </ul>
                        )}
                    </Droppable>
                </BlurHandler>

                {createPortal(
                    <ul className="x-tab-bar x-tab-drag-layer" ref={cloneLayer} />,
                    document.body,
                )}
            </DragDropContext>
        )
    }

    return (
        <>
            <ul className={cx("x-tab-bar", className)} ref={tabContainer} style={{ ...(styleObj || {}) }}>
                {tabs.map((tab, i) => {
                    return (
                        <li
                            key={(tab.id || i) + "-li"}
                            className={cx(tab.className, {
                                active: index === i,
                                hide: menuIndex !== undefined && i >= menuIndex,
                            })}
                            style={tab.style}
                            onClick={() => {
                                onChange(i)
                            }}
                        >
                            {tab.title}
                        </li>
                    )
                })}

                {menuIndex !== undefined && (
                    <li
                        className="x-tab-more"
                        onClick={(e) => {
                            e.persist()
                            setShowMenu(e)
                        }}
                    >
                        <PiDotsThreeOutlineVerticalFill />
                    </li>
                )}
            </ul>
            <BlurHandler
                onBlur={() => {
                    setShowMenu(null)
                }}
            >
                <ul
                    style={{ left: showMenu?.pageX, top: showMenu?.pageY }}
                    className={cx("x-tabs-more-menu", { open: Boolean(showMenu) })}
                >
                    {tabs.map((tab, i) => {
                        return (
                            <li
                                key={(tab.id || i) + "-li"}
                                className={cx(tab.className, {
                                    active: index === i,
                                    hide: i < menuIndex,
                                })}
                                style={tab.style}
                                onClick={() => {
                                    onChange(i)
                                    setShowMenu(null)
                                }}
                            >
                                {tab.title}
                            </li>
                        )
                    })}
                </ul>
            </BlurHandler>
        </>
    )
}

// `onReorder(ids)` makes the tabs draggable (bar and overflow menu); the parent owns the order.
const Tabs = ({ activeTabIndex, onChange, onReorder, data, className, cb, bodyClassName, returnArray, styleObj }) => {
    const [activeTab, setActiveTab] = useState(activeTabIndex || 0)

    // `data` can gain tabs after mount (Board adds PM/OERA/expenses tabs as they resolve), which
    // shifts the index of every tab after the insertion point. Controlled parents hand us a freshly
    // derived `activeTabIndex` in the same render as the new `data`, so syncing in an effect would
    // paint one frame of whatever tab now happens to sit at the old index - and, because the panel
    // is keyed on the index, unmount/remount the real tab either side of it. Adjust during render
    // instead: React re-runs this render before committing, so the stale index is never shown.
    const [syncedTabIndex, setSyncedTabIndex] = useState(activeTabIndex || 0)

    if (activeTabIndex !== undefined && activeTabIndex !== syncedTabIndex) {
        setSyncedTabIndex(activeTabIndex)
        setActiveTab(activeTabIndex)
    }

    const urlParams = new URLSearchParams(window.location.search)
    const org = urlParams.get("org")

    const cn = cx("dna-tabs-list", className)

    // Derived in render, so a reordered `data` paints in the drop frame with no snap back.
    const myTabs = useMemo(
        () =>
            data.map((tab, index) => {
                return {
                    id: tab.id || String(index),
                    title: <>{typeof tab.title === "string" ? tab.title : tab.title({ active: index === activeTab })}</>,
                    getContent: () => <></>,
                    className: cx("x-tab", tab.className),
                    panelClassName: "x-tab-panel",
                }
            }),
        [data, activeTab],
    )

    const theTab = data[activeTab]

    const tabList = (
        <TabBar
            styleObj={styleObj}
            tabs={myTabs}
            index={activeTab}
            className={cn}
            onReorder={onReorder}
            onChange={(tabIndex) => {
                if (data[tabIndex].cb) {
                    return data[tabIndex].cb(tabIndex, data[tabIndex])
                } else {
                    setActiveTab(tabIndex)
                    if (onChange) onChange(tabIndex, data[tabIndex])
                }
            }}
        />
    )

    const tabId = theTab?.id

    let tabPanel

    if (org === "666b0afd5b40852cdca0991b") {
        tabPanel =
            theTab && theTab.comp ? (
                <ErrorBoundary app={this} context={`tabs-${tabId}`}>
                    {theTab?.notComponent ? React.createElement(theTab?.comp, theTab.params) : theTab?.comp}
                </ErrorBoundary>
            ) : (
                <></>
            )
    } else {
        tabPanel = theTab?.comp ? (
            <AnimatePresence>
                <motion.div
                    // By id, so a reorder that moves the open tab doesn't remount it.
                    key={tabId || activeTab}
                    className={bodyClassName}
                    initial={{ opacity: 0, pointerEvents: "none" }}
                    animate={{ opacity: 1, pointerEvents: "all" }}
                    exit={{ opacity: 0, pointerEvents: "none" }}
                    transition={{ delay: 0.2, duration: 0.2 }}
                >
                    <ErrorBoundary app={this} context={`tabs-${tabId}`}>
                        {theTab?.notComponent ? React.createElement(theTab?.comp, theTab.params) : theTab?.comp}
                    </ErrorBoundary>
                </motion.div>
            </AnimatePresence>
        ) : (
            <></>
        )
    }

    if (returnArray) return [tabList, tabPanel]

    return (
        <>
            {tabList}
            {tabPanel}
        </>
    )
}

Tabs.displayName = "Tabs"
export default Tabs
